import { Application, Assets, Container, Graphics, ICanvas, Point } from "pixi.js";
import { DrawFrmThing } from "./DrawFrmThing";
import {Scene} from "./Scene";
import {DrawFrmReactManager, ShowReactInput} from "./DrawFrmDisplay";
import {EventEmitter} from "nate-react-api-helpers";

type RenderEl = () => any
export type DrawFrmAppOpts = {
    onShowReact: (element: RenderEl) => void;
    onHideReact: () => void;
    // backing-store pixel ratio for the renderer. Defaults to the device DPR for
    // interactive use; the off-screen export raises it to render more pixels so
    // small details stay sharp on the submittal grid. With autoDensity on, this
    // scales the backing store without changing the logical (scene-unit) layout.
    resolution?: number;
    // view only: scenes bind no drag or value editors, so the drawing can be looked at
    // and its elements selected but not changed.
    readOnly?: boolean;
}

export type RenderError = (props: {
    message: string;
    onDone(): void;
}) => any;

// MSDF (multi-channel signed distance field) bitmap font used for all in-scene
// text. baked from Arial via pixi-fonts/build.sh. MSDF stays crisp at any zoom,
// unlike raster Text which blurs when the scene is scaled. the .fnt registers a
// BitmapFont under the name "Arial" (its `face`); reference that via fontName.
const FONT_URL = "/fonts/Arial.fnt";

// grid-paper styling. spacing is in scene/world units (use util/drawingUnits to
// convert from imperial/metric). every `majorEvery`th line draws in majorColor,
// the rest in minorColor.
export type GridConfig = {
    spacing: number;
    majorEvery: number;
    minorColor: number;
    majorColor: number;
};

type RenderDimension = (props: {
    value: number;
    locked: boolean;
    onLockChange: (locked: boolean) => void;
    onDone: (value: number) => void;
    onCancel: () => void;
}) => any;

// dimension line + label styling. user-defined dimensions (ones the user has
// explicitly edited) render in `userColor`; everything else uses the default
// engineering-drawing styling. override `DrawFrmApp.dimensionStyle` (in whole or
// in part) from outside the package to theme dimensions per app instance.
export type DimensionStyle = {
    // stroke width of dimension/extension/tick lines, in shape units
    strokeWidth: number;
    // draw dimension/extension/tick lines as 1-device-pixel GL lines (pixi's
    // "native" lines) instead of stroking strokeWidth-wide quads. A stroke this
    // thin in scene units lands well under a pixel once the drawing is scaled to
    // fit, and the rasterizer then drops it depending on where the geometry falls
    // between pixel samples — the 45° end ticks blink out and back as the view
    // pans. Native lines are sized in device pixels, so they don't depend on the
    // scene scale. Turn off where the stroke is deliberately sized in scene units
    // (the export, which thickens it to survive the JPEG).
    nativeLines: boolean;
    // dimension line color for non-user-defined dimensions
    defaultLineColor: number;
    // label color for non-user-defined dimensions
    defaultLabelColor: number;
    // color used for both line and label of user-defined dimensions
    userColor: number;
};

export const defaultDimensionStyle: DimensionStyle = {
    strokeWidth: 0.5,
    nativeLines: true,
    defaultLineColor: 0x444444,
    defaultLabelColor: 0x000000,
    userColor: 0x1565c0,
};

export class DrawFrmApp {
    pixi: Application;
    private containerObserver: ResizeObserver;
    scene: Container;
    currentScene: Scene | null = null;

    // grid-paper layer; a child of `scene` so it pans/zooms with the drawing and
    // survives scene swaps (Scene.dispose only removes tracked items). Redrawn on
    // every transform to cover the visible region. Null config = no grid.
    private grid: Graphics;
    private gridConfig: GridConfig | null = null;

    // tracks the color last passed to setBackground so it can be read back (the
    // renderer's own background color isn't reliably round-trippable). Defaults to
    // the pixi clear color set in the constructor.
    private backgroundColor = 0xffffff;

    private panX = 0;
    private panY = 0;
    private scaleFactor = 1;
    private rotationRad = 0;

    private panning = false;
    private lastX = 0;
    private lastY = 0;

    // when true, a pointerdown on the canvas won't start a viewport pan. Set by
    // scene objects that own the drag (e.g. a lite being repositioned) so the
    // drag doesn't also pan the view; cleared when the drag ends.
    private panSuspended = false;

    private container: HTMLDivElement;
    private canvas: HTMLCanvasElement;

    opts: DrawFrmAppOpts;
    reactDisplay: DrawFrmReactManager;

    // see DrawFrmAppOpts.readOnly.
    readonly readOnly: boolean;

    refresh = new EventEmitter<number>();

    renderDimension: RenderDimension = () => {
        console.error("app.renderDimension not setup")
        return null
    };

    renderError: RenderError = () => {
        console.error("app.renderError not setup")
        return null
    };

    formatDimension = (value: number) => value.toFixed(2);

    // dimension line + label styling, overridable per app instance. defaults to
    // `defaultDimensionStyle`; assign a (partial via spread) object to customize.
    dimensionStyle: DimensionStyle = {...defaultDimensionStyle};

    // memoized so the font is fetched/parsed once across every app instance.
    private static fontsPromise: Promise<void> | null = null;

    constructor(container: HTMLDivElement, opts: DrawFrmAppOpts) {
        this.container = container;
        this.opts = opts;
        this.readOnly = !!opts.readOnly;

        // kick off font loading as soon as the app mounts so it's ready by the
        // time a scene needs to render text. callers must `await waitFonts()`
        // before drawing BitmapText (it throws if the font isn't loaded yet).
        void this.waitFonts();

        this.canvas = document.createElement("canvas");
        // absolutely positioned so PIXI's resizeTo drives the canvas from the
        // container's size, not the other way round: in normal flow the canvas'
        // own (PIXI-set) height feeds back into the flex container and stops it
        // shrinking when siblings (menubar / status bar) claim space. The
        // container is position:relative, so this fills it.
        this.canvas.style.position = "absolute";
        this.canvas.style.top = "0";
        this.canvas.style.left = "0";
        this.canvas.style.display = "block";
        container.appendChild(this.canvas);

        this.pixi = new Application({
            view: this.canvas,
            backgroundColor: 0xffffff,
            antialias: true,
            resolution: opts.resolution ?? (window.devicePixelRatio || 1),
            autoDensity: true,
            resizeTo: container,
        });

        this.scene = new Container();
        this.scene.sortableChildren = false;
        this.pixi.stage.addChild(this.scene);

        // grid sits at the back of the scene; items added later draw on top.
        this.grid = new Graphics();
        this.grid.eventMode = "none";
        this.scene.addChildAt(this.grid, 0);

        // re-cover the grid when the viewport resizes.
        this.pixi.renderer.on("resize", this.drawGrid);

        // resizeTo only follows window resizes; the container also changes size on its
        // own when a sibling (e.g. the hardware sidebar) opens or closes, so watch it.
        this.containerObserver = new ResizeObserver(() => this.pixi.queueResize());
        this.containerObserver.observe(container);

        this.reactDisplay = new DrawFrmReactManager(this);

        this.bindInteraction();
        this.applyTransform();
    }

    // resolves once the MSDF font is loaded and its BitmapFont is registered.
    // idempotent and shared across instances. await this before showing any
    // scene that renders text.
    waitFonts(): Promise<void> {
        if (!DrawFrmApp.fontsPromise) {
            DrawFrmApp.fontsPromise = Assets.load(FONT_URL).then(() => undefined);
        }
        return DrawFrmApp.fontsPromise;
    }

    showScene(input: Scene) {
        if (this.currentScene) {
            this.currentScene.dispose();
            this.reactDisplay.destroy();
        }

        this.reactDisplay = new DrawFrmReactManager(this);
        this.currentScene = input;
        input.setup(this);
    }

    showReact(input: ShowReactInput) {
        return this.reactDisplay.showReact(input);
    }

    private bindInteraction() {
        const c = this.canvas;

        c.addEventListener("pointerdown", this.onPointerDown);
        c.addEventListener("pointermove", this.onPointerMove);
        c.addEventListener("pointerup", this.onPointerUp);
        c.addEventListener("pointercancel", this.onPointerUp);
        c.addEventListener("pointerleave", this.onPointerUp);
        c.addEventListener("wheel", this.onWheel, { passive: false });
    }

    // while true, scenes keep their layout as it is (see Scene.applyLayout). A drag
    // that resizes the drawing sets it, so the drawing doesn't re-centre or re-scale
    // under the pointer.
    layoutFrozen = false;

    // suspend/resume viewport panning. Used by draggable scene objects so a drag
    // gesture doesn't also pan the canvas underneath it.
    setPanSuspended(suspended: boolean) {
        this.panSuspended = suspended;
    }

    // true while a draggable scene object owns the pointer (set via
    // setPanSuspended). Doubles as "an element gesture is in progress", which
    // click handlers can check to ignore the synthetic tap fired at the end of a
    // drag (pixi taps the common ancestor of the press + release targets).
    isPanSuspended(): boolean {
        return this.panSuspended;
    }

    private onPointerDown = (e: PointerEvent) => {
        if (this.panSuspended) return;
        this.panning = true;
        this.lastX = e.clientX;
        this.lastY = e.clientY;
        this.canvas.setPointerCapture(e.pointerId);
    };

    private onPointerMove = (e: PointerEvent) => {
        if (!this.panning) return;
        this.panX += e.clientX - this.lastX;
        this.panY += e.clientY - this.lastY;
        this.lastX = e.clientX;
        this.lastY = e.clientY;
        this.applyTransform();
    };

    private onPointerUp = (e: PointerEvent) => {
        if (!this.panning) return;
        this.panning = false;
        if (this.canvas.hasPointerCapture(e.pointerId)) {
            this.canvas.releasePointerCapture(e.pointerId);
        }
    };

    private onWheel = (e: WheelEvent) => {
        e.preventDefault();

        const rect = this.canvas.getBoundingClientRect();
        const cx = e.clientX - rect.left;
        const cy = e.clientY - rect.top;

        const delta = -e.deltaY * 0.0015;
        const next = Math.max(0.05, Math.min(50, this.scaleFactor * (1 + delta)));
        const ratio = next / this.scaleFactor;

        this.panX = cx - (cx - this.panX) * ratio;
        this.panY = cy - (cy - this.panY) * ratio;
        this.scaleFactor = next;

        this.applyTransform();
    };

    private applyTransform() {
        this.scene.position.set(this.panX, this.panY);
        this.scene.scale.set(this.scaleFactor);
        this.scene.rotation = this.rotationRad;
        this.drawGrid();
    }

    // set the background fill behind the scene.
    setBackground(color: number) {
        this.backgroundColor = color;
        this.pixi.renderer.background.color = color;
    }

    // apply the current dimension styling as the line style for dimension lines,
    // extension lines and end ticks. `color` is picked by the caller (user-defined
    // dimensions render in the user color).
    setDimensionLineStyle(g: Graphics, color: number) {
        g.lineStyle({
            width: this.dimensionStyle.strokeWidth,
            color: color,
            alpha: 1,
            native: this.dimensionStyle.nativeLines,
        });
    }

    // the current background fill color (as last set via setBackground).
    getBackground(): number {
        return this.backgroundColor;
    }

    // configure (or clear) the grid-paper layer and redraw it.
    setGrid(config: GridConfig | null) {
        this.gridConfig = config;
        this.drawGrid();
    }

    // the current grid-paper config, or null when the grid is off.
    getGrid(): GridConfig | null {
        return this.gridConfig;
    }

    // redraw grid lines to cover the currently-visible world region. Lines live
    // in scene (world) space, so widths are divided by the zoom to stay ~1px on
    // screen. Arrow fn so it can be used directly as a resize listener.
    private drawGrid = () => {
        const g = this.grid;
        g.clear();

        const cfg = this.gridConfig;
        if (!cfg) return;

        // how far apart minor lines are on screen at the current zoom. Skip the
        // grid when that gets too small — it'd be visual noise and would draw a
        // huge number of lines when zoomed far out.
        if (cfg.spacing * this.scaleFactor < 6) return;

        const w = this.pixi.screen.width;
        const h = this.pixi.screen.height;

        // visible region in scene-local (world) coords. toLocal accounts for the
        // scene's pan/scale/pivot/rotation.
        const corners = [
            this.scene.toLocal(new Point(0, 0)),
            this.scene.toLocal(new Point(w, 0)),
            this.scene.toLocal(new Point(0, h)),
            this.scene.toLocal(new Point(w, h)),
        ];

        let minX = Infinity, minY = Infinity, maxX = -Infinity, maxY = -Infinity;
        for (const p of corners) {
            if (p.x < minX) minX = p.x;
            if (p.x > maxX) maxX = p.x;
            if (p.y < minY) minY = p.y;
            if (p.y > maxY) maxY = p.y;
        }

        const { spacing, majorEvery, minorColor, majorColor } = cfg;
        const startI = Math.floor(minX / spacing), endI = Math.ceil(maxX / spacing);
        const startJ = Math.floor(minY / spacing), endJ = Math.ceil(maxY / spacing);
        const lineWidth = 1 / this.scaleFactor;

        // minor lines first, then major on top so the heavier lines win at
        // intersections.
        g.lineStyle(lineWidth, minorColor, 1);
        for (let i = startI; i <= endI; i++) {
            if (i % majorEvery === 0) continue;
            const x = i * spacing;
            g.moveTo(x, minY); g.lineTo(x, maxY);
        }
        for (let j = startJ; j <= endJ; j++) {
            if (j % majorEvery === 0) continue;
            const y = j * spacing;
            g.moveTo(minX, y); g.lineTo(maxX, y);
        }

        g.lineStyle(lineWidth, majorColor, 1);
        for (let i = startI; i <= endI; i++) {
            if (i % majorEvery !== 0) continue;
            const x = i * spacing;
            g.moveTo(x, minY); g.lineTo(x, maxY);
        }
        for (let j = startJ; j <= endJ; j++) {
            if (j % majorEvery !== 0) continue;
            const y = j * spacing;
            g.moveTo(minX, y); g.lineTo(maxX, y);
        }
    };

    // reset the user-applied pan/zoom (e.g. from onWheel) back to the identity
    // transform. Does not touch scene-space layout — pair with
    // Scene.applyLayout() to fully reset the view.
    resetView() {
        this.panX = 0;
        this.panY = 0;
        this.scaleFactor = 1;
        this.applyTransform();
    }

    setScale(scale: number) {
        this.scaleFactor = scale;
        this.applyTransform();
    }

    getScale(): number {
        return this.scaleFactor;
    }

    setRotation(rad: number) {
        this.rotationRad = rad;
        this.applyTransform();
    }

    getRotation(): number {
        return this.rotationRad;
    }

    setPan(x: number, y: number) {
        this.panX = x;
        this.panY = y;
        this.applyTransform();
    }

    getPan(): { x: number; y: number } {
        return { x: this.panX, y: this.panY };
    }

    add(thing: DrawFrmThing) {
        this.scene.addChild(thing.graphic);
    }

    remove(thing: DrawFrmThing) {
        this.scene.removeChild(thing.graphic);
    }

    async exportToImage(): Promise<string> {
        // exclude the grid paper from exported drawings.
        this.grid.visible = false;
        try {
            return await this.pixi.renderer.extract.base64(this.scene);
        } finally {
            this.grid.visible = true;
        }
    }

    // export the current scene as a lossless PNG blob for submittals. PNG (vs JPEG)
    // avoids the compression ringing that softened thin dimension lines and small
    // text. The scene extract is transparent where nothing is drawn, so it's
    // flattened onto solid white first — submittals want a white sheet, not
    // transparency (which would otherwise encode as black in a non-alpha consumer).
    async exportToPngBlob(): Promise<Blob> {
        // exclude the grid paper from exported drawings.
        this.grid.visible = false;
        try {
            const src = this.pixi.renderer.extract.canvas(this.scene) as HTMLCanvasElement;

            const out = document.createElement("canvas");
            out.width = src.width;
            out.height = src.height;

            const ctx = out.getContext("2d");
            if(!ctx) throw new Error("2d context unavailable");
            ctx.fillStyle = "#ffffff";
            ctx.fillRect(0, 0, out.width, out.height);
            ctx.drawImage(src, 0, 0);

            return await new Promise<Blob>((resolve, reject) => {
                out.toBlob(
                    b => b ? resolve(b) : reject(new Error("canvas.toBlob returned null")),
                    "image/png",
                );
            });
        } finally {
            this.grid.visible = true;
        }
    }

    destroy() {
        if (this.currentScene) {
            this.currentScene.dispose();
            this.currentScene = null;
        }

        this.containerObserver.disconnect();
        this.reactDisplay.destroy();

        const c = this.canvas;
        c.removeEventListener("pointerdown", this.onPointerDown);
        c.removeEventListener("pointermove", this.onPointerMove);
        c.removeEventListener("pointerup", this.onPointerUp);
        c.removeEventListener("pointercancel", this.onPointerUp);
        c.removeEventListener("pointerleave", this.onPointerUp);
        c.removeEventListener("wheel", this.onWheel);

        this.pixi.destroy(true, { children: true });
    }

    showError(s: string) {
        console.warn(s);

        this.refresh.emit(Date.now())

        this.showReact({
            render: (opt) => this.renderError({
                message: s,
                onDone: () => opt.remove(),
            }),
        })
    }
}

export type { ICanvas };
